import React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useAdminUserEmailReportsQuery } from '../../api/admin';
import AdminQuickFilterToolbar from './AdminQuickFilterToolbar';

const WEEKDAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

interface Props {
	userId: number;
	/** Fill the parent (a flex column) instead of using a fixed viewport-based height. */
	fill?: boolean;
}

function ChipList({ items }: { items: string[] }) {
	if (!items || items.length === 0) {
		return null;
	}
	return (
		<Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
			{items.map((item) => (
				<Chip key={item} label={item} size="small" color="secondary" variant="outlined" />
			))}
		</Stack>
	);
}

export default function UserEmailReportsTable({ userId, fill }: Props) {
	const { data: reports, isLoading } = useAdminUserEmailReportsQuery(userId);

	const columns: GridColDef[] = [
		{
			field: 'name',
			headerName: 'Name',
			flex: 1,
		},
		{
			field: 'report_type',
			headerName: 'Type',
			flex: 1,
		},
		{
			field: 'weekday_schedule',
			headerName: 'Weekdays',
			flex: 1,
			valueFormatter: ({ value }) => ((value ?? []) as number[]).map((d) => WEEKDAY_NAMES[d]).join(', '),
		},
		{
			field: 'delta_days',
			headerName: 'Delta Days',
			flex: 1,
			type: 'number',
		},
		{
			field: 'top_n',
			headerName: 'Top N',
			flex: 1,
			type: 'number',
		},
		{
			field: 'watchlist_names',
			headerName: 'Watchlists',
			flex: 1,
			sortable: false,
			filterable: false,
			renderCell: (params: GridRenderCellParams) => (
				<ChipList items={params.value as string[]} />
			),
		},
		{
			field: 'include_attachment',
			headerName: 'Attachment',
			flex: 1,
			valueFormatter: ({ value }) => (value == null ? '' : value ? 'Yes' : 'No'),
		},
		{
			field: 'field_name',
			headerName: 'Field',
			flex: 1,
		},
		{
			field: 'report_params',
			headerName: 'Params',
			flex: 1,
			valueFormatter: ({ value }) =>
				value ? Object.entries(value).map(([k, v]) => `${k}: ${v}`).join(', ') : '',
		},
		{
			field: 'created_at',
			headerName: 'Created',
			flex: 1,
			valueFormatter: ({ value }) =>
				value ? new Date(value).toLocaleDateString('en-CA') : '',
		},
	];

	return (
		<Box sx={fill ? { flex: 1, minHeight: 0 } : { height: 'calc(100vh - 310px)', minHeight: 300 }}>
			<DataGrid
				rows={reports ?? []}
				columns={columns}
				loading={isLoading}
				disableSelectionOnClick
				components={{ Toolbar: AdminQuickFilterToolbar }}
				getRowHeight={() => 'auto'}
				density="compact"
			/>
		</Box>
	);
}
